import { yupResolver } from "@hookform/resolvers/yup";
import { debounce } from "@mui/material";
import { isEmpty, isEqual } from "lodash";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";
import {
  PersonalizedGiftTextForm,
  useGiftEmailCreatorFormStore,
} from "@/store/views/onlineStore/giftEmailCreatorFormStore";

export const giftFormSchema = yup.object().shape({
  recipientFirstName: yup
    .string()
    .trim()
    .max(32)
    .required("Please provide recipient's first name"),
  recipientLastName: yup
    .string()
    .trim()
    .max(32)
    .required("Please provide recipient's last name"),
  recipientEmail: yup
    .string()
    .email("Please provide a valid email")
    .required("Please provide recipient's email"),
  recipientPhone: yup
    .string()
    .test(
      "isValidPhoneNumber",
      "Please provide a valid phone number",
      (value: string) => {
        return isValidPhoneNumber(value || "");
      }
    ),
});

export const personalizedGiftFormSchema = giftFormSchema.concat(
  yup.object().shape({
    senderName: yup
      .string()
      .trim()
      .max(32)
      .required("Please provide sender's name"),
  })
);

// util for consistent text form data extraction
const getTextFieldsFormData = (formData: PersonalizedGiftTextForm) => ({
  recipientFirstName: formData.recipientFirstName || "",
  recipientLastName: formData.recipientLastName || "",
  recipientEmail: formData.recipientEmail || "",
  recipientPhone: formData.recipientPhone || "",
  senderName: formData.senderName || "",
  customGiftMessage: formData.customGiftMessage || "",
});

export function useGiftTextFieldsForm(
  defaultFormData: PersonalizedGiftTextForm
) {
  const form = useForm<PersonalizedGiftTextForm>({
    resolver: yupResolver(personalizedGiftFormSchema),
    defaultValues: getTextFieldsFormData(defaultFormData),
  });
  const setZustandFormData = useGiftEmailCreatorFormStore(
    (state) => state.setTextFieldsData
  );

  const synchronizeWithPersistedStore = useMemo(
    () =>
      debounce((formValues: PersonalizedGiftTextForm) => {
        const currentZustandFormData = getTextFieldsFormData(
          useGiftEmailCreatorFormStore.getState()
        );
        const incomingFormData = getTextFieldsFormData(formValues);
        if (!isEqual(incomingFormData, currentZustandFormData)) {
          setZustandFormData(incomingFormData);
        }
      }, 300),
    [setZustandFormData]
  );

  const { watch } = form;
  const formValues = watch() as PersonalizedGiftTextForm;

  useEffect(() => {
    if (!isEmpty(formValues)) {
      synchronizeWithPersistedStore(formValues);
    }
  }, [formValues, synchronizeWithPersistedStore]);

  return form;
}
